import React, { useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { ApolloError } from '@apollo/client';

import {
  ModifierGroup,
  // eslint-disable-next-line camelcase
  Menus_VisibilityEnum,
  useDoMenuItemQuery,
  useMenuItemDetailsQuery
} from 'src/apollo/onlineOrdering';
import { Channel } from 'src/public/js/siteUtilities';

import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay';
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider';
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext';
import NoMatch404 from 'shared/components/no_match_404/NoMatch404';

import { MenuItem } from 'public/components/default_template/menu_section/MenuSection';
import OrderPageWrapper, { OrderPageContentProps } from 'public/components/default_template/online_ordering/pages/OrderPageWrapper';
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext';
import { ModifierContextProvider } from 'public/components/online_ordering/ModifierContext';
import { Action, collapseModifierGroups } from 'public/components/online_ordering/types';

import { ProductDisplay } from './ProductDisplay';

type LocationState = {
  menuItem?: MenuItem
}

const ProductDisplayPageContent = ({ itemGuid, itemGroupGuid }: OrderPageContentProps) => {
  const ooClient = useOOClient();
  const { restaurant: siteRestaurant, selectedLocation } = useRestaurant();
  const channelGuid = selectedLocation?.ecommMenuChannelGuid || undefined;
  const { state } = useLocation<LocationState>();
  const restaurantGuid = selectedLocation?.externalId || '';
  const { fulfillmentData } = useFulfillment();
  const shouldSkip = !restaurantGuid || !itemGuid || !itemGroupGuid;

  const { data, error, loading: menuItemDetailsLoading } = useMenuItemDetailsQuery({
    variables: {
      input: {
        itemGuid: itemGuid || '',
        itemGroupGuid: itemGroupGuid || '',
        restaurantGuid: selectedLocation?.externalId || '',
        channelGuid,
        dateTime: fulfillmentData?.cartFulfillmentData.fulfillmentDateTime
      },
      nestingLevel: 10
    },
    client: ooClient,
    skip: shouldSkip
  });

  // massage menuItemDetails into the MenuItem type
  const menuItem: MenuItem | undefined = data?.menuItemDetails ?
    { ...data?.menuItemDetails, guid: itemGuid || null, itemGroupGuid: itemGroupGuid || '' }
    : state?.menuItem;
  const loading = !menuItem && menuItemDetailsLoading;

  const { data: doMenuItemData } = useDoMenuItemQuery({
    variables: {
      input: {
        menuGroupGuid: itemGroupGuid || '',
        menuItemGuid: itemGuid || '',
        restaurantGuid,
        // eslint-disable-next-line camelcase
        visibility: Menus_VisibilityEnum.ToastOnlineOrdering,
        dateTime: fulfillmentData?.cartFulfillmentData.fulfillmentDateTime,
        channelGuid
      }
    },
    client: ooClient,
    skip: shouldSkip
  });


  const rootRef = useRef<HTMLDivElement>(null);
  const modifierGroups = useMemo(() => data?.menuItemDetails.modifierGroups ? collapseModifierGroups(data?.menuItemDetails.modifierGroups) : {}, [data]);
  const rootModifier = {
    groupGuid: itemGroupGuid || 'root',
    modifier: {
      itemGuid: itemGuid!,
      itemGroupGuid,
      modifierGroups: data?.menuItemDetails.modifierGroups || [],
      price: data?.menuItemDetails.prices[0] || 0
    },
    modifierGroups,
    isRoot: true,
    masterId: data?.menuItemDetails?.masterId,
    action: 'replace' as Action,
    price: data?.menuItemDetails.prices[0] || 0,
    usesFractionalQuantity: data?.menuItemDetails?.usesFractionalQuantity,
    unitOfMeasure: data?.menuItemDetails?.unitOfMeasure,
    name: data?.menuItemDetails.name
  };

  const modifierMap = doMenuItemData?.doMenus_findMenuItem?.__typename === 'DoMenus_FindMenuItemResponse'
    ? doMenuItemData?.doMenus_findMenuItem?.menuResponse?.modifierGroupReferences?.reduce((map, ref) => ({
      ...map,
      [ref.guid]: ref
    }), {})
    : {};

  return (
    <div className="pageContent">
      <div className="paddedContentWrapper">
        {error && itemNotFound(error) && <NoMatch404 meta={siteRestaurant?.meta} siteTheme={siteRestaurant?.theme} /> }
        {!loading && !error && menuItem &&
          <ModifierContextProvider modifier={rootModifier} rootRef={rootRef} modifierGroupData={modifierMap} quantity={1}>
            <ProductDisplay menuItem={menuItem} modifierGroups={data?.menuItemDetails?.modifierGroups as ModifierGroup[] || []} />
          </ModifierContextProvider>}
        {loading && <LoadingSpinnerOverlay withBorderRadius={false} fullScreen={true} />}
      </div>
    </div>
  );
};

const itemNotFound = (error: ApolloError | undefined) => error?.graphQLErrors?.some((err: any) => err?.extensions?.code === 'MENU_ITEM_NOT_FOUND');

const ProductDisplayPage = () => {
  return (
    <OrderPageWrapper channel={Channel.ECOMM}>
      {props =>
        <ProductDisplayPageContent
          {...props} />}
    </OrderPageWrapper>);
};
export default React.memo(ProductDisplayPage);
